import React, { useState } from "react";
import { parseISO, format } from "date-fns";
import Calendar, { CalendarProps } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { CalendarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLocale } from "use-intl";
import { translations } from "@/messages";
type DatePickerInputProps = {
  elementId: string;
  label: string | undefined;
  disabled?: boolean;
  elementExtraAttributes?: {
    isAgeInput?: false;
  } | {
    isAgeInput: true;
    minAge: number;
  } | null;
};
const DatePickerInput = ({
  elementId,
  label,
  disabled = false,
  elementExtraAttributes
}: DatePickerInputProps) => {
  const form = useFormContext();
  const locale = useLocale() as keyof typeof translations;
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [open, setOpen] = useState(false);
  const checkIfValidDate = (date: unknown): boolean => {
    if (typeof date === "string") {
      try {
        const parsedDate = parseISO(date);
        return !isNaN(parsedDate.getTime());
      } catch (error) {
        console.error("Invalid date:", error);
        return false;
      }
    }
    return false;
  };
  const handleDateChange: CalendarProps["onChange"] = value => {
    if (value instanceof Date) {
      setSelectedDate(value);
      form.setValue(elementId, format(value, "yyyy-MM-dd"));
      setOpen(false);
    } else if (Array.isArray(value)) {
      console.warn("Date ranges are not supported in this configuration.");
    } else {
      console.warn("Unexpected null value for selected date.");
    }
  };
  return <>
      <style>
        {`
          .react-calendar {
            border: none;
            border-radius: 16px;
            background-color: hsl(var(--background) / .8);
            backdrop-filter: blur(10px);
            color: #000000;
            padding: 10px;
            font-family: inherit;
          }

          .react-calendar__navigation button {
            background: transparent;
            color: inherit;
            border: none;
            padding: 5px;
            cursor: pointer;
          }

          .react-calendar__navigation button:hover {
            background-color: rgba(59, 130, 246, 0.1);
          }

          .react-calendar__tile {
            background: transparent;
            color: inherit;
            padding: 8px;
            font-weight: 500;
          }

          .react-calendar__tile--active {
            background: #2563eb;
            color: #ffffff;
          }

          .react-calendar__tile--hover {
            background: #3b82f6;
            color: #ffffff;
          }

          .react-calendar__tile--disabled {
            background: transparent;
            color: #9ca3af;
            cursor: not-allowed;
          }

          .dark .react-calendar {
            color: #d1d5db;
          }

          .dark .react-calendar__navigation button:hover {
            background-color: rgba(59, 130, 246, 0.2);
          }

          .dark .react-calendar__tile--active {
            background: #3b82f6;
            color: #ffffff;
          }

          .dark .popover-content {
            background-color: #1f2937;
            border: 1px solid #374151;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
          }

          .react-calendar__navigation button:enabled:hover, 
          .react-calendar__navigation button:enabled:focus {
              background-color: #e6e6e6;
          }

          .dark .react-calendar__navigation button:enabled:hover, 
          .dark .react-calendar__navigation button:enabled:focus {
              background-color: #444;
          }

          .react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus {
              background-color: #e6e6e6;
          }

          

          .dark .react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus {
              background-color: #444;
          }

          .react-calendar button {
            border-radius: 50%;
            min-height: 46px;
            min-width: 46px;
            transition: background-color 0.1s ease;
          }

          .react-calendar__navigation__label, .react-calendar__tile {
            border-radius: 16px !important;
          }

          .react-calendar abbr {
            text-decoration: none;
          }

        `}
      </style>
      <FormField control={form.control} name={elementId} render={({
      field
    }) => {
      return <FormItem className="w-full">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button disabled={disabled} variant={"outline"} className={cn("h-12 w-full rounded-3xl pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                      {field.value && checkIfValidDate(field.value) ? format(parseISO(field.value), "PPP") : <span>{label || "Pick a date"}</span>}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto overflow-hidden rounded-3xl p-0" align="start">
                  <Calendar onChange={handleDateChange} value={selectedDate || (field.value ? parseISO(field.value) : null)} selectRange={false} maxDetail="month" />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>;
    }} data-sentry-element="FormField" data-sentry-source-file="DatePickerInput.tsx" />
    </>;
};
export default DatePickerInput;